/**
 * notificationRouter — THE single source of truth for where a bell /
 * notification row goes when tapped. Both the active home (HomeScreen.js)
 * and the legacy home (HomePage.js) call `routeNotification(n, ctx)` so the
 * two surfaces can never drift apart again. (User ask 2026-10-04: "ALL
 * notifications need to be routed accordingly and correctly… we should only
 * have 1 handler.")
 *
 * ctx = {
 *   nav(route, meta?)       navigate to a full page
 *   openSheet(key)          open a slide-up sheet (HomeScreen); legacy falls back to nav
 *   openNotifications()     open the bell panel (DM inbox alerts)
 *   setEodReport(obj)       open the EOD report modal in place
 *   crewId                  signed-in crew id (for interactive actions)
 *   closePanel()            close the bell panel before routing
 * }
 */
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stash = (key, value) => {
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* quota */ }
};

export function routeNotification(n, ctx = {}) {
  const {
    nav = () => {},
    openSheet,
    openNotifications,
    setEodReport,
    crewId = "",
    closePanel = () => {},
  } = ctx;
  // openSheet falls back to nav on surfaces (legacy home) with no slide-ups.
  const sheet = typeof openSheet === "function" ? openSheet : (k) => nav(k);

  closePanel();
  if (!n) return;
  const type = n.type || "";

  // ── Recognition / High Fives / Member of the Month ──────────────────
  // Deep-link to the RIGHT surface: a store-wide MOTM announcement opens
  // the Member-of-the-Month spotlight (so EVERYONE sees the winner, not
  // their own empty board); a personal High Five opens the recipient's
  // received-cards board and replays the celebration. (User report
  // 2026-10-04: MOTM notif took everyone to their own board, winner
  // never got seen.)
  if (type === "recognition_motm" || type === "recognition_motm_store" || type === "motm") {
    stash("wow:openRecognition", { kind: "motm", motm_id: n.motm_id || "", winner_id: n.winner_id || "", month_key: n.month_key || "" });
    nav("recognition");
    return;
  }
  if (type === "recognition") {
    stash("wow:openRecognition", { kind: "recognition", recognition_id: n.recognition_id || "" });
    try { window.dispatchEvent(new Event("wow:recognition-celebrate")); } catch { /* ignore */ }
    nav("recognition");
    return;
  }

  // ── EOD report ready → open the on-demand report modal in place ─────
  if (type === "eod_ready") {
    if (typeof setEodReport === "function") setEodReport({ date: n.target_date, store_id: n.store_id });
    else nav("reports");
    return;
  }
  if (type === "eod_digest") { nav("reports"); return; }

  // ── Coverage tasks ──────────────────────────────────────────────────
  if (type === "coverage_completion" || type === "coverage_handoff") {
    stash("wow:openCoverageTask", { task_id: n.task_id, date: n.date, store_id: n.store_id });
    nav("manager");
    return;
  }
  if (type === "coverage_help_request") {
    const taskUuid = String(n.task_id || "").replace(/^coverage:/, "");
    // eslint-disable-next-line no-alert
    if (!window.confirm(`${n.message}\n\nTap OK to let them know you're jumping in to help.`)) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/zone-sheets/${n.date}/coverage-tasks/summary`, {
          params: { actor_id: crewId, store_id: n.store_id || "" },
        });
        const items = (r.data && r.data.items) || [];
        const row = items.find((it) => it.task_id === taskUuid);
        if (!row) { toast.error("Task no longer exists."); return; }
        await axios.post(
          `${API}/zone-sheets/${n.date}/coverage-tasks/transition`,
          { actor_id: crewId, crew_id: row.crew_id, task_id: taskUuid, transition: "accept_help" },
          { params: { store_id: n.store_id || "" } },
        );
        toast.success(`You're helping ${row.crew_name || "your teammate"} with ${row.label}.`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't accept help request.");
      }
    })();
    return;
  }
  if (type === "coverage_help_accepted" || type === "coverage_nudge") { nav("home"); return; }
  if (type === "coverage_overdue" || type === "shift_end_handoff") { nav("zone-sheet"); return; }

  // ── Checkpoints / Eyes on the Biz ───────────────────────────────────
  if (type === "checkpoint" || type === "checkpoint_reminder" || type === "eod_missing") {
    const slot = n.slot || (() => {
      const parts = String(n.id || "").split("_");
      const idx = parts.indexOf("reminder");
      return idx >= 0 && parts[idx + 2] ? parts[idx + 2] : "";
    })();
    stash("wow:openCheckpoint", { slot, date: n.date || "", store_id: n.store_id || "" });
    nav("zone-sheet");
    return;
  }
  if (type === "ai_win_plan_ready") { nav("zone-sheet"); return; }

  // ── Zone sheet / tasks / shift nudges ───────────────────────────────
  if (type === "zone_assignment" || type === "task" || type === "wtd_shift_nudge") { nav("zone-sheet"); return; }

  // ── Attendance ──────────────────────────────────────────────────────
  if (type === "attendance_self" || type === "attendance_threshold") { nav("home"); return; }

  // ── Birthdays / anniversaries / celebration blast → the card wall ───
  // Open the celebration wall, not just Home. The stored blast notification
  // has type "celebration" (no honoree), while per-person pushes use
  // birthday*/anniversary* types and may carry honoree_id/kind/date so we can
  // open the exact card. Fire `wow:open-birthday`; App.js opens the wall.
  if (type === "celebration" || n.route === "birthday"
      || type.startsWith("birthday") || type.startsWith("anniversary")) {
    nav("home");
    const card = n.honoree_id
      ? { honoree_id: n.honoree_id, date: n.date || "", kind: n.kind || "anniversary",
          years: n.years || 0, first_name: n.first_name || n.honoree_name || "" }
      : null;
    try { window.dispatchEvent(new CustomEvent("wow:open-birthday", { detail: { card } })); } catch { /* ignore */ }
    return;
  }

  // ── Bug reports ─────────────────────────────────────────────────────
  if (type === "bug_report") { nav("admin", { initialView: "bug_reports" }); return; }
  if (type === "bug_report_self" || type === "bug_report_status") { nav("inbox"); return; }

  // ── Moderation / transfers / position / pin → admin or home ─────────
  // A flagged-message tap now carries the message_id into a sessionStorage
  // stash so AdminPage can scroll to the new Flagged Messages review list
  // and highlight the exact message. (User report 2026-10-11: tapping the
  // notification opened Admin but showed nothing — the id was dropped and
  // no review surface existed.)
  if (type === "moderation_flagged") {
    stash("wow:openFlagged", { message_id: n.message_id || "" });
    nav("admin");
    return;
  }
  if (type === "transfer_request" || type === "pin_reset_request") { nav("admin"); return; }
  if (type === "transfer_approved" || type === "transfer_rejected" || type === "position_change") { nav("home"); return; }
  // Suspension: tapping the pause bell opens the member's suspension screen
  // (reason → appeal). Appeal tickets go to the admin queue; appeal results
  // and restores just land on home (the bell message says what happened).
  if (type === "crew_suspended") {
    nav("home");
    try { window.dispatchEvent(new CustomEvent("wow:open-suspension")); } catch { /* ignore */ }
    return;
  }
  if (type === "crew_restored" || type === "suspension_appeal_result") { nav("home"); return; }
  if (type === "suspension_appeal") {
    stash("wow:openAppeals", { appeal_id: n.appeal_id || "" });
    nav("admin");
    return;
  }

  // ── Store closure → settings ────────────────────────────────────────
  if (type === "store_closure") { nav("settings"); return; }

  // ── Stories & walkie → home ─────────────────────────────────────────
  if (type.startsWith("story_") || type === "voice_emergency" || type === "voice_raise_hand" || type === "recovery_world" || type === "push_reminder") { nav("home"); return; }

  // ── DM-level unread-alerts bell row → open the bell panel ───────────
  if (type === "dm_inbox_alerts") { if (typeof openNotifications === "function") openNotifications(); return; }

  // ── Everything else (DMs, mentions, reactions, group, blasts, notes,
  //     urgent alerts) lives in the Inbox. ──────────────────────────────
  nav("inbox");
}

export default routeNotification;
